import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { BOOKING_URL, FOUNDER } from "@/lib/constants";
import { CalendarCheck, Mail, Globe, CheckCircle2 } from "lucide-react";

/**
 * Reusable final CTA / consultation block for the Workforce Blueprint.
 * Kept modular so future additions (Calendly, video intro, testimonials, etc.)
 * can be dropped in without redesigning the page.
 */
export const ConsultationCTA = ({ bookingUrl = BOOKING_URL, founder = FOUNDER }) => {
  const openBooking = () => window.open(bookingUrl, "_blank", "noopener,noreferrer");

  const reviewItems = [
    "Your recommended team structure",
    "Role responsibilities and priorities",
    "Implementation roadmap and next steps",
    "Candidate sourcing timeline",
    "Questions specific to your business",
  ];

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45 }}
      data-testid="consultation-cta"
    >
      {/* Letter from the Founder */}
      <div
        className="rounded-2xl border border-white/10 p-8 sm:p-10"
        style={{ backgroundColor: "#0F1424" }}
        data-testid="founder-note"
      >
        <div className="text-xs uppercase tracking-[0.2em] text-[#66B2FF] font-semibold mb-4">
          A Personal Note
        </div>
        <div className="space-y-4 text-white/80 leading-relaxed max-w-3xl">
          <p>Thank you for using TeamBuilder AI.</p>
          <p>
            I created this assessment to help business owners quickly visualize what a
            scalable support organization could look like before committing to a
            consultation.
          </p>
          <p>
            While every recommendation is generated using our assessment engine, every
            implementation is customized around your unique workflows, goals, and culture.
          </p>
          <p>
            If you'd like to discuss your Workforce Blueprint, I'd be happy to personally
            review it with you, answer your questions, and help determine whether
            Rendeavored is the right partner for your business.
          </p>
          <p>I look forward to speaking with you.</p>
        </div>
        <div className="mt-6">
          <div className="text-white font-semibold">{founder.name}</div>
          <div className="text-white/55 text-sm">{founder.title}</div>
          <div className="text-white/55 text-sm">{founder.company}</div>
        </div>
      </div>

      {/* Consultation CTA */}
      <div className="mt-10 sm:mt-14 grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Ready to discuss your Workforce Blueprint?
          </h2>
          <p className="text-white/65 mt-3 max-w-lg">
            Let's review your assessment together and build an implementation plan
            tailored to your business.
          </p>

          <div className="mt-6 space-y-1.5 text-white/80">
            <div className="font-semibold text-white">{founder.name}</div>
            <div className="text-sm text-white/55">{founder.title}</div>
            <div className="text-sm text-white/55">{founder.company}</div>
            <div className="flex items-center gap-2 text-sm pt-2">
              <Mail size={15} className="text-[#66B2FF]" />
              <a href={`mailto:${founder.email}`} className="text-[#66B2FF] hover:underline" data-testid="founder-email-link">
                {founder.email}
              </a>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Globe size={15} className="text-[#66B2FF]" />
              <a href={founder.website} target="_blank" rel="noopener noreferrer" className="text-[#66B2FF] hover:underline" data-testid="founder-website-link">
                {founder.website}
              </a>
            </div>
          </div>

          <Button
            data-testid="schedule-consultation-button"
            onClick={openBooking}
            className="mt-7 bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-xl h-12 px-6 text-base shadow-[0_10px_30px_rgba(30,107,255,0.25)]"
          >
            <CalendarCheck size={18} className="mr-2" /> Schedule My Complimentary Consultation
          </Button>
        </div>

        <div
          className="rounded-2xl border border-white/10 p-7"
          style={{ backgroundColor: "#101a33" }}
        >
          <div className="text-white font-semibold mb-4">During our consultation we'll review:</div>
          <ul className="space-y-3">
            {reviewItems.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-white/80 text-sm">
                <CheckCircle2 size={18} className="text-[#66B2FF] shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.section>
  );
};

export default ConsultationCTA;
